import './dateSummary.scss';

import { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Timeslot } from '~types/appsync.types';
import { extractTime } from '~utils';

interface DateSummaryProps {
  startDate?: Date;
  endDate?: Date;
  locale: string;
  displayCalendar?: boolean;
  timeslot?: Timeslot;
}

const formatDate = (date: Date, locale: string) => {
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
  });
};

const DateSummary = ({
  startDate,
  endDate,
  locale,
  displayCalendar,
  timeslot,
}: DateSummaryProps) => {
  const showEndDate = !!endDate;
  const hasTimeslot = !!timeslot;
  const showHour = !!(timeslot && !timeslot.dailyBooking);
  const showSeats = !!(timeslot && !timeslot.quotaUnlimited);
  const seatsCount = timeslot ? timeslot.quotaAvailable : 0;

  const formattedStartDate = startDate ? formatDate(startDate, locale) : '';
  const formattedEndDate = endDate ? formatDate(endDate, locale) : '';
  const formattedTimeslotHour = showHour && timeslot ? extractTime(timeslot.date) : '';

  if (!displayCalendar) {
    return (
      <div className='DateSummary'>
        <FormattedMessage id='undatedProductMessage' defaultMessage='No date required' />
      </div>
    );
  }

  if (!startDate) {
    return (
      <div className='DateSummary_placeholder'>
        <FormattedMessage id='noDateSelected' defaultMessage='No date selected' />
      </div>
    );
  }

  return (
    <div className='DateSummary'>
      <span className='DateSummary_date'>{formattedStartDate}</span>
      {showEndDate && (
        <>
          <span className='DateSummary_arrow'> - </span>
          <span className='DateSummary_date'>{formattedEndDate}</span>
        </>
      )}
      {hasTimeslot && (
        <>
          {showHour && (
            <>
              <span className='DateSummary_arrow'> - </span>
              <span className='DateSummary_date'>{formattedTimeslotHour}</span>
            </>
          )}
          {showSeats && (
            <span className='DateSummary_date'>
              {' ('}
              <FormattedMessage
                defaultMessage='{count, plural, one {# seat} other {# seats}}'
                id='seats'
                values={{ count: seatsCount }}
              />
              {')'}
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default memo(DateSummary);
